import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useStore } from 'react-redux'
import type { RootState } from '@/store'
import { toast } from 'sonner'
import {
  instructionsApi,
  useCreateInstructionMutation,
  useUpdateInstructionMutation,
  useDeleteInstructionMutation,
} from '@/features/instructions/instructionsApi'
import type { Instruction, InstructionUpdate } from '@/features/instructions/types'
import { articlePatch, type ArticleForm } from '@/features/instructions/lib/articleDraft'
import { errorMessage } from '@/features/instructions/lib/errorMessage'
import { computeTimeToRead } from '@/features/instructions/lib/computeTimeToRead'
import { articleGap, articleReadingSource } from '@/features/instructions/lib/articleGap'
import { parseModerationError } from '@/features/moderation/lib/parseModerationError'
import { ARTICLE_SAVE_REFUSED } from '@/features/moderation/lib/reviewInProgress'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

const fail = (text: string): null => {
  toast.error(text)
  return null
}
const info = (text: string): null => {
  toast.info(text)
  return null
}

/** Успешная правка: ушедший патч и момент, когда клиент получил ответ `PATCH`. */
export interface SavedPatch {
  patch: InstructionUpdate
  /** `fulfilledTimeStamp` мутации: запрос, начатый не раньше, читает уже записанное. */
  savedAt: number
}

interface Args {
  isCreate: boolean
  instruction: Instruction | undefined
}

/** Момент ответа мутации (`fulfilledTimeStamp`); нет в кеше — «сейчас». */
const fulfilledAt = (sub: { fulfilledTimeStamp?: number }): number =>
  sub.fulfilledTimeStamp ?? Date.now()

export const useEditorActions = ({ isCreate, instruction }: Args) => {
  const navigate = useNavigate()
  const store = useStore<RootState>()
  const [createInstruction, { isLoading: creating }] = useCreateInstructionMutation()
  const [updateInstruction, { isLoading: updating }] = useUpdateInstructionMutation()
  const [deleteInstruction] = useDeleteInstructionMutation()
  // Редактор размонтирован (например, «плюс» открыл чистую новую статью, пока создание
  // летело) — созданная статья не должна перехватить адрес и следующие сохранения.
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  // Правка шлёт дифф с засевом (живое + черновик), а не форму целиком: эхо всех полей
  // живой статьи стёрло бы её черновик (контракт §4a). Возвращает ушедший патч только при
  // успешном `PATCH` — по нему форма строит базу ребейза (`useArticleForm.markSent`).
  // `silent` — автосохранение: без тостов успеха и подсказок; ошибки сети всё равно тостом.
  const save = async (
    form: ArticleForm,
    seed: ArticleForm,
    { silent = false }: { silent?: boolean } = {},
  ): Promise<SavedPatch | null> => {
    const m = messagesNow(messages)
    const gap = articleGap(form)
    if (gap) return silent ? null : fail(gap === 'title' ? m.fillTitle : m.fillContent)
    try {
      if (isCreate) {
        const body = { ...form, timeToRead: computeTimeToRead(articleReadingSource(form)) }
        const request = createInstruction(body)
        const created = await request.unwrap()
        const done = instructionsApi.endpoints.createInstruction.select(request.requestId)
        const savedAt = fulfilledAt(done(store.getState()))
        if (!silent) toast.success(m.saved)
        if (alive.current) navigate(`/instructions/${created.slug}`, { replace: true })
        // Как у правки: ушедшая форма — база ребейза. Без неё засев созданной статьи с
        // нормализованным сервером текстом держал форму грязной — после автосохранения
        // кнопка оставалась «Сохранить» до ручного клика.
        return { patch: body, savedAt }
      } else if (instruction) {
        const patch = articlePatch(form, seed)
        if (Object.keys(patch).length === 0) return silent ? null : info(m.noChanges)
        const request = updateInstruction({ id: instruction.id, patch })
        await request.unwrap()
        const done = instructionsApi.endpoints.updateInstruction.select(request.requestId)
        const savedAt = fulfilledAt(done(store.getState()))
        if (!silent) toast.success(m.saved)
        return { patch, savedAt }
      }
    } catch (err) {
      // `updateInstruction` молчит в `errorToastMiddleware`: отказ объясняет только этот тост.
      // `409 review_in_progress` — заморозка встала между тиками опроса.
      toast.error(
        parseModerationError(err).kind === 'in_progress' ? ARTICLE_SAVE_REFUSED : errorMessage(err),
      )
    }
    return null
  }

  const remove = async (force = false) => {
    if (!instruction) return
    const m = messagesNow(messages)
    if (!force && !window.confirm(m.deleteConfirm)) return
    try {
      await deleteInstruction(instruction.id).unwrap()
      toast.success(m.deleted)
      navigate('/instructions')
    } catch (err) {
      toast.error(errorMessage(err))
    }
  }

  return { save, remove, saving: creating || updating }
}
